import { html, css, LitElement, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { hermesClient } from "../hermes-singleton.js";
import type { PolicySkill, PolicySkillConfig, PolicySkillSummary } from "../../types/ead-types.ts";
import {
  loadCachedPolicySkillSummaries,
  saveCachedPolicySkillSummaries,
} from "../policy-skills-cache.ts";
import { isEadSystemAdmin } from "../ead-admin.ts";
import {
  buildSkillReportMarkdown,
  buildSkillScriptBlockMarkdown,
  compileEadScriptToMermaid,
  countEadScriptElements,
  diagramGeneratePromptFor,
  mermaidFromCurrentEadScript,
  skillScriptFor,
} from "../lib/skill-report-markdown.ts";
import { policySkillCodeReference } from "../lib/policy-skill-references.ts";
import { notifyOperator } from "../operator-notify.ts";
import "../components/ead-skill-report-view.ts";
import "../components/ead-mermaid-diagram.ts";

function summaryFromRow(row: Record<string, unknown>): PolicySkillSummary {
  return {
    skillKey: String(row.skillKey ?? row.skill_key ?? ""),
    displayName: String(row.displayName ?? row.display_name ?? ""),
    note: row.note != null ? String(row.note) : "",
    sortOrder: Number(row.sortOrder ?? row.sort_order ?? 0),
    enabled: row.enabled !== false && row.enabled !== 0,
    updatedAt: Number(row.updatedAt ?? row.updated_at ?? 0),
  };
}

function configFromRow(row: Record<string, unknown>): PolicySkillConfig | undefined {
  const raw = row.configJson ?? row.config_json;
  if (raw && typeof raw === "object") {
    return raw as PolicySkillConfig;
  }
  if (typeof raw === "string" && raw.trim()) {
    try {
      return JSON.parse(raw) as PolicySkillConfig;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

function skillFromRow(row: Record<string, unknown>): PolicySkill {
  const summary = summaryFromRow(row);
  return {
    ...summary,
    bodyMarkdown: String(row.bodyMarkdown ?? row.body_markdown ?? ""),
    configJson: configFromRow(row),
  };
}

function normalizeSummaries(rows: Array<PolicySkillSummary | Record<string, unknown>>): PolicySkillSummary[] {
  return rows
    .map((row) => summaryFromRow(row as Record<string, unknown>))
    .filter((row) => Boolean(row.skillKey));
}

function skillRowFromResponse(resp: Record<string, unknown>): Record<string, unknown> {
  const nested = resp.skill;
  if (nested && typeof nested === "object") {
    return nested as Record<string, unknown>;
  }
  return resp;
}

function isViewOnlySkill(skill: PolicySkill | null): boolean {
  const cfg = skill?.configJson;
  return Boolean(cfg?.viewOnly ?? cfg?.view_only);
}

@customElement("ead-skills-view")
export class EadSkillsView extends LitElement {
  static styles = css`
    :host { display: block; height: 100%; }
    .container { display: flex; height: 100%; flex-direction: column; }
    .breadcrumb { padding: 12px 28px 0; font-size: 13px; color: var(--muted, #8b8d93); }
    .layout { display: flex; flex: 1; min-height: 0; }
    .skill-list {
      width: 380px; min-width: 380px;
      border-right: 1px solid var(--border, #2e2f33);
      overflow-y: auto;
      background: rgba(255,255,255,0.01);
    }
    .skill-list-header { padding: 20px 24px; border-bottom: 1px solid var(--border, #2e2f33); }
    .skill-list-header h2 { margin: 0; font-size: 20px; font-weight: 650; letter-spacing: -0.02em; color: #f8fafc; }
    .skill-item { padding: 16px 24px; border-bottom: 1px solid var(--border, #2e2f33); cursor: pointer; }
    .skill-item:hover { background: rgba(255,255,255,0.03); }
    .skill-item.selected { background: rgba(255,255,255,0.06); border-left: 3px solid var(--accent, #6ea8fe); }
    .skill-item .name { font-size: 17px; font-weight: 560; margin-bottom: 6px; line-height: 1.4; color: #f1f5f9; }
    .skill-item .key { font-size: 13px; color: #94a3b8; font-family: 'JetBrains Mono', 'SF Mono', Monaco, monospace; line-height: 1.45; }
    .skill-item .key-label {
      font-size: 11px; font-weight: 600; color: var(--muted, #8b8d93);
      text-transform: uppercase; letter-spacing: 0.35px; margin-bottom: 4px;
    }
    .detail-panel { flex: 1; min-height: 0; display: flex; flex-direction: column; padding: 0; }
    .detail-toolbar {
      display: flex; align-items: center; justify-content: space-between; gap: 16px;
      padding: 16px 24px; border-bottom: 1px solid var(--border, #2e2f33);
      background: rgba(255,255,255,0.02); flex-shrink: 0;
    }
    .detail-toolbar-title h3 {
      margin: 0; font-size: 1.05rem; font-weight: 650; color: #f8fafc; letter-spacing: -0.01em;
    }
    .detail-toolbar-title .subtitle {
      font-size: 13px; color: var(--muted, #8b8d93); margin-top: 4px;
      font-family: 'JetBrains Mono', Monaco, monospace;
    }
    .detail-toolbar-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
    .menu-anchor { position: relative; display: inline-flex; }
    .btn-menu {
      padding: 7px 12px; border-radius: 6px; border: 1px solid var(--border, #2e2f33);
      background: transparent; color: var(--fg, #e4e5e7); font-size: 16px; line-height: 1;
      cursor: pointer; letter-spacing: 0.12em;
    }
    .btn-menu:hover { background: rgba(255,255,255,0.05); }
    .menu-panel {
      position: absolute; top: calc(100% + 6px); right: 0; min-width: 168px;
      border: 1px solid var(--border, #2e2f33); border-radius: 8px;
      background: var(--bg-surface, #222326); box-shadow: 0 12px 32px rgba(0,0,0,0.42);
      padding: 6px; z-index: 40;
    }
    .menu-item {
      display: block; width: 100%; text-align: left; padding: 8px 12px; border: none;
      border-radius: 6px; background: transparent; color: var(--fg, #e4e5e7);
      font-size: 13px; cursor: pointer;
    }
    .menu-item:hover { background: rgba(255,255,255,0.06); }
    .menu-item--danger { color: #fca5a5; }
    .menu-item--danger:hover { background: rgba(239,68,68,0.12); }
    .confirm-backdrop {
      position: fixed; inset: 0; z-index: 1200; display: grid; place-items: center;
      padding: 24px; background: rgba(0, 0, 0, 0.58); backdrop-filter: blur(8px);
    }
    .confirm-card {
      width: min(520px, calc(100vw - 40px));
      border: 1px solid color-mix(in srgb, var(--danger, #ef4444) 28%, var(--border, #2e2f33));
      border-radius: 16px;
      background:
        radial-gradient(circle at top left, color-mix(in srgb, var(--danger, #ef4444) 14%, transparent), transparent 38%),
        var(--bg-elevated, #161920);
      box-shadow: 0 28px 80px rgba(0, 0, 0, 0.48); overflow: hidden;
    }
    .confirm-header {
      display: flex; gap: 12px; padding: 18px 20px 12px; align-items: flex-start;
    }
    .confirm-icon {
      width: 34px; height: 34px; border-radius: 12px; display: grid; place-items: center;
      background: color-mix(in srgb, var(--danger, #ef4444) 16%, transparent);
      color: var(--danger, #ef4444); font-weight: 800; flex: 0 0 auto;
    }
    .confirm-title { margin: 0; font-size: 16px; color: #f4f4f5; font-weight: 750; }
    .confirm-subtitle {
      margin-top: 5px; font-size: 12px; color: var(--muted, #8b8d93); line-height: 1.5;
      font-family: 'JetBrains Mono', 'SF Mono', Monaco, monospace;
    }
    .confirm-body {
      padding: 0 20px 16px 66px; display: grid; gap: 10px; color: #d4d4d8;
      font-size: 13px; line-height: 1.55;
    }
    .confirm-warning {
      padding: 10px 12px; border-radius: 10px;
      border: 1px solid color-mix(in srgb, #f59e0b 28%, transparent);
      background: color-mix(in srgb, #f59e0b 11%, transparent); color: #fcd34d;
    }
    .confirm-error {
      padding: 10px 12px; border-radius: 10px;
      border: 1px solid color-mix(in srgb, #ef4444 30%, transparent);
      background: color-mix(in srgb, #ef4444 12%, transparent); color: #fca5a5;
    }
    .confirm-actions {
      display: flex; justify-content: flex-end; gap: 8px; padding: 0 20px 18px;
    }
    .btn-danger {
      background: color-mix(in srgb, #ef4444 88%, #000); color: #fff;
      border-color: color-mix(in srgb, #ef4444 70%, transparent);
    }
    .btn-danger:hover { filter: brightness(1.08); }
    .detail-body { flex: 1; min-height: 0; display: flex; flex-direction: column; padding: 20px 24px; }
    .skill-view-scroll { flex: 1; min-height: 0; overflow-y: auto; }
    .skill-view-panel {
      border: 1px solid rgba(148, 163, 184, 0.22);
      border-radius: 10px;
      background: linear-gradient(180deg, rgba(15, 15, 22, 0.98) 0%, rgba(8, 8, 12, 0.99) 100%);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.04);
      padding: 2rem 2.5rem 2.5rem;
    }
    .ead-script-section { margin-top: 0.25rem; }
    .ead-script-header {
      display: flex; align-items: center; justify-content: space-between; gap: 12px;
      margin: 1.75rem 0 0.85rem; padding-bottom: 0.35rem;
      border-bottom: 1px solid rgba(148, 163, 184, 0.18);
    }
    .ead-script-header h2 {
      margin: 0; font-size: 1.05rem; font-weight: 650; color: #f1f5f9;
    }
    .ead-script-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
    .btn-sm { padding: 5px 12px; font-size: 12px; }
    .edit-scroll { flex: 1; min-height: 0; overflow-y: auto; padding-right: 4px; }
    .field-group { margin-bottom: 18px; }
    .field-group label {
      display: block; font-size: 12px; font-weight: 600; color: var(--muted, #8b8d93);
      margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.35px;
    }
    .field-group label.readonly-hint,
    .field-group .readonly-hint {
      text-transform: none; font-weight: 500; font-size: 11px; color: #94a3b8;
      margin-top: -2px; margin-bottom: 8px;
    }
    .field-group input[type="text"], .field-group textarea {
      width: 100%; box-sizing: border-box; padding: 10px 12px; border-radius: 8px;
      border: 1px solid var(--border, #2e2f33); background: var(--bg-surface, #222326);
      color: var(--fg, #e4e5e7); font-size: 14px; font-family: inherit; line-height: 1.55;
    }
    .field-group textarea.body { min-height: 160px; resize: vertical; }
    .field-group textarea.script {
      min-height: 280px; resize: vertical;
      font-family: 'JetBrains Mono', 'SF Mono', Monaco, monospace;
      font-size: 13px; line-height: 1.5;
    }
    .field-group textarea.prompt {
      min-height: 220px; resize: vertical; line-height: 1.55;
    }
    .preview-stats {
      font-size: 12px; color: #94a3b8; margin-bottom: 10px;
    }
    .preview-stats.warn { color: #ffb74d; }
    .empty-script {
      color: #94a3b8; font-size: 14px; line-height: 1.55; padding: 0.5rem 0 0.25rem;
    }
    .modal-backdrop {
      position: fixed; inset: 0; z-index: 1100;
      background: rgba(0, 0, 0, 0.6);
      display: grid; place-items: center; padding: 12px;
    }
    .diagram-modal {
      width: min(98vw, 1480px);
      max-height: calc(100vh - 24px);
      display: flex; flex-direction: column;
      background: var(--bg, #161920);
      border: 1px solid var(--border, #2e2f33);
      border-radius: 12px;
      box-shadow: 0 22px 48px rgba(0, 0, 0, 0.48);
      overflow: hidden;
    }
    .diagram-modal-header {
      display: flex; align-items: center; justify-content: space-between; gap: 12px;
      padding: 10px 14px; border-bottom: 1px solid var(--border, #2e2f33);
      background: rgba(255, 255, 255, 0.02); flex-shrink: 0;
    }
    .diagram-modal-header h3 { margin: 0; font-size: 16px; color: #f8fafc; }
    .diagram-modal-header .subtitle { font-size: 12px; color: var(--muted, #8b8d93); margin-top: 2px; }
    .diagram-modal-body {
      flex: 1; min-height: 0; overflow-y: auto; overflow-x: hidden;
      padding: 0.35rem 0.5rem 0.75rem;
    }
    .diagram-modal-body ead-mermaid-diagram {
      min-height: 0; display: block; width: 100%;
    }
    .actions { display: none; }
    .dirty-inline { font-size: 12px; color: #ffb74d; margin-right: 4px; }
    .btn { padding: 7px 16px; border-radius: 6px; border: 1px solid var(--border, #2e2f33); background: transparent; color: var(--fg, #e4e5e7); font-size: 13px; cursor: pointer; }
    .btn:hover { background: rgba(255,255,255,0.05); }
    .btn-primary { background: var(--accent, #6ea8fe); color: #fff; border-color: var(--accent, #6ea8fe); }
    .btn:disabled { opacity: 0.5; cursor: not-allowed; }
    .dirty { font-size: 12px; color: #ffb74d; }
    .badge { display: inline-block; font-size: 11px; padding: 2px 8px; border-radius: 999px; background: rgba(110,168,254,0.15); color: var(--accent, #6ea8fe); margin-left: 8px; vertical-align: middle; }
    .loading, .empty { display: flex; align-items: center; justify-content: center; height: 100%; color: var(--muted, #8b8d93); font-size: 14px; padding: 20px 24px; }
    .detail-panel > .dirty, .detail-panel > .empty { padding: 20px 24px; }
  `;

  @state() private _summaries: PolicySkillSummary[] = normalizeSummaries(loadCachedPolicySkillSummaries() || []);
  @state() private _selectedKey = "";
  @state() private _detail: PolicySkill | null = null;
  @state() private _draft: PolicySkill | null = null;
  @state() private _detailLoading = false;
  @state() private _loading = false;
  @state() private _saving = false;
  @state() private _error = "";
  @state() private _editMode = false;
  @state() private _diagramModalOpen = false;
  @state() private _generateModalOpen = false;
  @state() private _generatePromptDraft = "";
  @state() private _generating = false;
  @state() private _savingPrompt = false;
  @state() private _diagramRefreshKey = 0;
  @state() private _scriptPreviewOpen = false;
  @state() private _pendingGeneratedScript = "";
  @state() private _savingGeneratedScript = false;
  @state() private _menuOpen = false;
  @state() private _deleteConfirm: PolicySkill | null = null;
  @state() private _deleting = false;
  @state() private _deleteError = "";

  private _detailLoadSeq = 0;
  private _onDocClick = (): void => {
    if (this._menuOpen) this._menuOpen = false;
  };

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener("click", this._onDocClick);
    void this._loadList().then(() => this._loadInitialSelection());
  }

  disconnectedCallback(): void {
    document.removeEventListener("click", this._onDocClick);
    super.disconnectedCallback();
  }

  private _serializeDraft(skill: PolicySkill) {
    return {
      displayName: skill.displayName,
      bodyMarkdown: skill.bodyMarkdown || "",
      script: skillScriptFor(skill),
    };
  }

  private get _dirty(): boolean {
    if (!this._detail || !this._draft) return false;
    return JSON.stringify(this._serializeDraft(this._detail)) !== JSON.stringify(this._serializeDraft(this._draft));
  }

  private get _canEdit(): boolean {
    if (!this._draft) return false;
    if (isViewOnlySkill(this._draft)) {
      return isEadSystemAdmin();
    }
    return true;
  }

  private _liveMermaidSource(draft: PolicySkill): string {
    return mermaidFromCurrentEadScript(draft, skillScriptFor(draft));
  }

  private _hasDiagramSource(draft: PolicySkill): boolean {
    return Boolean(skillScriptFor(draft).trim() || this._liveMermaidSource(draft));
  }

  private async _loadList(): Promise<void> {
    this._loading = !this._summaries.length;
    this._error = "";
    const resp = await hermesClient.request<{ skills?: Record<string, unknown>[] }>("policySkills.list");
    this._loading = false;
    if (resp?.skills) {
      this._summaries = normalizeSummaries(resp.skills);
      saveCachedPolicySkillSummaries(this._summaries);
      this._error = "";
    } else if (!this._summaries.length) {
      this._error = hermesClient.getLastRequestError() || "Could not load policy skills.";
      return;
    } else {
      this._error = hermesClient.getLastRequestError() || "Could not refresh policy skills list.";
    }
  }

  private _resolveSelectedKey(): string {
    if (this._selectedKey && this._summaries.some((s) => s.skillKey === this._selectedKey)) {
      return this._selectedKey;
    }
    return this._summaries[0]?.skillKey || "";
  }

  private _fallbackDraftFromSummary(skillKey: string): PolicySkill | null {
    const summary = this._summaries.find((s) => s.skillKey === skillKey);
    if (!summary) return null;
    return {
      ...summary,
      bodyMarkdown: summary.note || "",
      configJson: {},
    };
  }

  private async _loadDetail(skillKey: string): Promise<void> {
    if (!skillKey) return;
    const loadSeq = ++this._detailLoadSeq;
    this._detailLoading = true;
    this._editMode = false;
    this._diagramModalOpen = false;
    this._generateModalOpen = false;
    this._scriptPreviewOpen = false;
    this._pendingGeneratedScript = "";
    const cached = this._fallbackDraftFromSummary(skillKey);
    if (cached) {
      this._draft = cached;
    }
    try {
      const resp = await hermesClient.request<Record<string, unknown>>("policySkills.get", { skillKey });
      if (loadSeq !== this._detailLoadSeq) return;
      if (!resp || typeof resp !== "object" || !Object.keys(resp).length) {
        this._error = hermesClient.getLastRequestError() || "Could not load skill detail.";
        this._detail = null;
        this._draft = this._fallbackDraftFromSummary(skillKey);
        return;
      }
      this._detail = skillFromRow(skillRowFromResponse(resp));
      this._draft = this._cloneDraft(this._detail);
      this._error = "";
    } finally {
      if (loadSeq === this._detailLoadSeq) {
        this._detailLoading = false;
      }
    }
  }

  private async _loadInitialSelection(): Promise<void> {
    const key = this._resolveSelectedKey();
    if (!key) return;
    this._selectedKey = key;
    await this._loadDetail(key);
  }

  private _cloneDraft(source: PolicySkill): PolicySkill {
    return {
      ...source,
      configJson: {
        ...(source.configJson || {}),
        eadScript: skillScriptFor(source),
      },
    };
  }

  private async _selectSkill(skillKey: string): Promise<void> {
    if (this._dirty && !confirm("You have unsaved changes. Discard them?")) return;
    this._selectedKey = skillKey;
    await this._loadDetail(skillKey);
  }

  private _enterEdit(): void {
    if (!this._canEdit) return;
    this._editMode = true;
  }

  private _exitEdit(): void {
    if (this._dirty && !confirm("Discard unsaved changes?")) return;
    this._editMode = false;
    if (this._detail) {
      this._draft = this._cloneDraft(this._detail);
    }
  }

  private async _save(): Promise<void> {
    if (!this._draft) return;
    this._saving = true;
    this._error = "";
    const resp = await hermesClient.request<Record<string, unknown>>("policySkills.update", {
      skillKey: this._draft.skillKey,
      displayName: this._draft.displayName,
      bodyMarkdown: this._draft.bodyMarkdown,
      configJson: { eadScript: skillScriptFor(this._draft) },
    });
    this._saving = false;
    if (!resp) {
      this._error = hermesClient.getLastRequestError() || "Save failed.";
      return;
    }
    this._detail = skillFromRow(resp);
    this._draft = this._cloneDraft(this._detail);
    this._editMode = false;
    await this._loadList();
  }

  private _revert(): void {
    if (this._detail) {
      this._draft = this._cloneDraft(this._detail);
    }
  }

  private _updateDraft(patch: Partial<PolicySkill>): void {
    if (!this._draft) return;
    this._draft = { ...this._draft, ...patch };
  }

  private _updateSkillScript(value: string): void {
    if (!this._draft) return;
    this._draft = {
      ...this._draft,
      configJson: { ...(this._draft.configJson || {}), eadScript: value },
    };
  }

  private _openDiagramModal(): void {
    this._diagramRefreshKey += 1;
    this._diagramModalOpen = true;
  }

  private _closeDiagramModal(): void {
    this._diagramModalOpen = false;
  }

  private _regenerateDiagramFromScript(): void {
    this._diagramRefreshKey += 1;
  }

  private _openGenerateModal(): void {
    if (!this._draft) return;
    this._generatePromptDraft = diagramGeneratePromptFor(this._draft);
    this._generateModalOpen = true;
  }

  private _closeGenerateModal(): void {
    if (this._generating || this._savingPrompt) return;
    this._generateModalOpen = false;
  }

  private _updateGeneratePromptDraft(value: string): void {
    this._generatePromptDraft = value;
  }

  private async _saveGeneratePrompt(): Promise<void> {
    if (!this._draft || !this._canEdit) return;
    this._savingPrompt = true;
    this._error = "";
    const prompt = this._generatePromptDraft.trim();
    const resp = await hermesClient.request<Record<string, unknown>>("policySkills.update", {
      skillKey: this._draft.skillKey,
      configJson: {
        ...(this._draft.configJson || {}),
        eadDiagramGeneratePrompt: prompt,
      },
    });
    this._savingPrompt = false;
    if (!resp) {
      this._error = hermesClient.getLastRequestError() || "Could not save diagram prompt.";
      return;
    }
    this._detail = skillFromRow(resp);
    this._draft = this._cloneDraft(this._detail);
  }

  private async _runGenerateEadScript(): Promise<void> {
    if (!this._draft || !this._canEdit || this._generating) return;
    this._generating = true;
    this._error = "";
    const prompt = this._generatePromptDraft.trim();
    const resp = await hermesClient.request<Record<string, unknown>>("policySkills.generateEadScript", {
      skillKey: this._draft.skillKey,
      generatePrompt: prompt,
      savePrompt: true,
      persist: false,
    });
    this._generating = false;
    if (!resp) {
      this._error = hermesClient.getLastRequestError() || "EAD script generation failed.";
      return;
    }
    const generated = String(resp.eadScript ?? resp.ead_script ?? "").trim();
    if (!generated) {
      this._error = "EAD script generation returned empty text.";
      return;
    }
    this._pendingGeneratedScript = generated;
    this._generateModalOpen = false;
    this._scriptPreviewOpen = true;
  }

  private _cancelGeneratedScript(): void {
    if (this._savingGeneratedScript) return;
    this._scriptPreviewOpen = false;
    this._pendingGeneratedScript = "";
  }

  private async _saveGeneratedScript(): Promise<void> {
    if (!this._draft || !this._canEdit || !this._pendingGeneratedScript.trim()) return;
    this._savingGeneratedScript = true;
    this._error = "";
    const script = this._pendingGeneratedScript.trim();
    const mermaid = compileEadScriptToMermaid(script);
    const resp = await hermesClient.request<Record<string, unknown>>("policySkills.update", {
      skillKey: this._draft.skillKey,
      configJson: {
        ...(this._draft.configJson || {}),
        eadScript: script,
        ...(mermaid ? { eadMermaidMap: mermaid } : {}),
      },
    });
    this._savingGeneratedScript = false;
    if (!resp) {
      this._error = hermesClient.getLastRequestError() || "Could not save generated EAD Script.";
      return;
    }
    this._detail = skillFromRow(resp);
    this._draft = this._cloneDraft(this._detail);
    this._scriptPreviewOpen = false;
    this._pendingGeneratedScript = "";
    this._diagramRefreshKey += 1;
    this._diagramModalOpen = true;
  }

  private _renderToolbarMenu(): ReturnType<typeof html> {
    if (!this._canEdit) return nothing;
    return html`
      <span class="menu-anchor">
        <button
          type="button"
          class="btn-menu"
          aria-label="More actions"
          @click=${(e: Event) => {
            e.stopPropagation();
            this._menuOpen = !this._menuOpen;
          }}
        >
          ⋯
        </button>
        ${this._menuOpen
          ? html`
              <div class="menu-panel" @click=${(e: Event) => e.stopPropagation()}>
                <button
                  type="button"
                  class="menu-item menu-item--danger"
                  @click=${() => this._openDeleteConfirm()}
                >
                  Delete
                </button>
              </div>
            `
          : nothing}
      </span>
    `;
  }

  private _openDeleteConfirm(): void {
    if (!this._draft || !this._canEdit) return;
    this._menuOpen = false;
    this._deleteError = "";
    this._deleteConfirm = this._draft;
  }

  private _closeDeleteConfirm(): void {
    if (this._deleting) return;
    this._deleteConfirm = null;
    this._deleteError = "";
  }

  private async _confirmDeleteSkill(): Promise<void> {
    const pending = this._deleteConfirm;
    if (!pending || this._deleting) return;
    this._deleting = true;
    this._deleteError = "";
    const deletedKey = pending.skillKey;
    const resp = await hermesClient.request<{ ok?: boolean }>("policySkills.delete", {
      skillKey: deletedKey,
    });
    this._deleting = false;
    if (!resp?.ok) {
      this._deleteError = hermesClient.getLastRequestError() || "Could not delete this EAD Skill.";
      return;
    }
    this._deleteConfirm = null;
    this._editMode = false;
    this._detail = null;
    this._draft = null;
    this._selectedKey = "";
    await this._loadList();
    const nextKey = this._resolveSelectedKey();
    if (nextKey) {
      await this._loadDetail(nextKey);
    }
    notifyOperator({
      title: "EAD Skill deleted",
      message: `"${pending.displayName}" (${deletedKey}) was removed.`,
      type: "success",
      modal: true,
    });
  }

  private _renderDeleteConfirmModal(): ReturnType<typeof html> {
    const pending = this._deleteConfirm;
    if (!pending) return nothing;
    const codeRef = policySkillCodeReference(pending.skillKey);
    return html`
      <div class="confirm-backdrop" @click=${() => this._closeDeleteConfirm()}>
        <div
          class="confirm-card"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-skill-title"
          @click=${(e: Event) => e.stopPropagation()}
        >
          <div class="confirm-header">
            <div class="confirm-icon">!</div>
            <div>
              <h3 id="delete-skill-title" class="confirm-title">Delete EAD Skill?</h3>
              <div class="confirm-subtitle">${pending.displayName} · ${pending.skillKey}</div>
            </div>
          </div>
          <div class="confirm-body">
            <div>
              This removes the skill from the gateway database. Runs will no longer receive this
              skill text unless you recreate it or run policy skill seed.
            </div>
            ${codeRef
              ? html`
                  <div class="confirm-warning">
                    Referenced by gateway code: ${codeRef}
                  </div>
                `
              : nothing}
            ${this._deleteError ? html`<div class="confirm-error">${this._deleteError}</div>` : nothing}
          </div>
          <div class="confirm-actions">
            <button class="btn" type="button" ?disabled=${this._deleting} @click=${() => this._closeDeleteConfirm()}>
              Not now
            </button>
            <button
              class="btn btn-danger"
              type="button"
              ?disabled=${this._deleting}
              @click=${() => void this._confirmDeleteSkill()}
            >
              ${this._deleting ? "Deleting…" : "Delete skill"}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private _renderScriptPreviewModal(draft: PolicySkill) {
    if (!this._scriptPreviewOpen || !this._pendingGeneratedScript) return nothing;
    const stats = countEadScriptElements(this._pendingGeneratedScript);
    const overLimit = stats.totalEntities > 10 || stats.actionCount > 12;
    const previewMermaid = compileEadScriptToMermaid(this._pendingGeneratedScript);
    return html`
      <div class="modal-backdrop" @click=${() => this._cancelGeneratedScript()}>
        <div
          class="diagram-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ead-script-preview-title"
          @click=${(e: Event) => e.stopPropagation()}
        >
          <div class="diagram-modal-header">
            <div>
              <h3 id="ead-script-preview-title">Generated EAD Script</h3>
              <div class="subtitle">Review before saving · ${draft.skillKey}</div>
            </div>
            <button class="btn" ?disabled=${this._savingGeneratedScript} @click=${() => this._cancelGeneratedScript()}>
              Close
            </button>
          </div>
          <div class="diagram-modal-body">
            <div class="preview-stats ${overLimit ? "warn" : ""}">
              Entities: ${stats.totalEntities}/10 · Actions: ${stats.actionCount}/12
              ${overLimit ? " · Over simplicity limits — edit after save or regenerate." : ""}
            </div>
            <div class="field-group">
              <label>Generated EAD Script</label>
              <textarea class="script" readonly .value=${this._pendingGeneratedScript}></textarea>
            </div>
            ${previewMermaid
              ? html`
                  <ead-mermaid-diagram
                    .source=${previewMermaid}
                    .showHeading=${false}
                    .showSourceToggle=${true}
                    .embedded=${true}
                    .refreshKey=${this._diagramRefreshKey}
                  ></ead-mermaid-diagram>
                `
              : nothing}
            <div class="detail-toolbar-actions" style="justify-content: flex-end; padding-top: 12px;">
              <button class="btn" ?disabled=${this._savingGeneratedScript} @click=${() => this._cancelGeneratedScript()}>
                Cancel
              </button>
              <button
                class="btn btn-primary"
                ?disabled=${this._savingGeneratedScript}
                @click=${() => void this._saveGeneratedScript()}
              >
                ${this._savingGeneratedScript ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private _renderGenerateModal(draft: PolicySkill) {
    if (!this._generateModalOpen) return nothing;
    return html`
      <div class="modal-backdrop" @click=${() => this._closeGenerateModal()}>
        <div
          class="diagram-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ead-generate-modal-title"
          @click=${(e: Event) => e.stopPropagation()}
        >
          <div class="diagram-modal-header">
            <div>
              <h3 id="ead-generate-modal-title">Generate EAD Diagram</h3>
              <div class="subtitle">EAD Skill Key: ${draft.skillKey}</div>
            </div>
            <button class="btn" ?disabled=${this._generating || this._savingPrompt} @click=${() => this._closeGenerateModal()}>
              Close
            </button>
          </div>
          <div class="diagram-modal-body">
            <div class="field-group">
              <label>AI Prompt for EAD Diagram</label>
              <div class="readonly-hint">Saved per skill. Edit before generating the EAD Script.</div>
              <textarea
                class="prompt"
                .value=${this._generatePromptDraft}
                ?disabled=${!this._canEdit || this._generating || this._savingPrompt}
                @input=${(e: Event) => this._updateGeneratePromptDraft((e.target as HTMLTextAreaElement).value)}
              ></textarea>
            </div>
            <div class="detail-toolbar-actions" style="justify-content: flex-end; padding-top: 4px;">
              <button class="btn" ?disabled=${this._generating || this._savingPrompt} @click=${() => this._closeGenerateModal()}>
                Cancel
              </button>
              ${this._canEdit
                ? html`
                    <button
                      class="btn"
                      ?disabled=${this._generating || this._savingPrompt || !this._generatePromptDraft.trim()}
                      @click=${() => void this._saveGeneratePrompt()}
                    >
                      ${this._savingPrompt ? "Saving…" : "Save Prompt"}
                    </button>
                    <button
                      class="btn btn-primary"
                      ?disabled=${this._generating || this._savingPrompt || !this._generatePromptDraft.trim()}
                      @click=${() => void this._runGenerateEadScript()}
                    >
                      ${this._generating ? "Generating…" : "Generate"}
                    </button>
                  `
                : nothing}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private _renderDiagramModal(draft: PolicySkill) {
    if (!this._diagramModalOpen) return nothing;
    const mermaidSource = this._liveMermaidSource(draft);
    const hasScript = Boolean(skillScriptFor(draft).trim());
    return html`
      <div class="modal-backdrop" @click=${() => this._closeDiagramModal()}>
        <div
          class="diagram-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ead-diagram-modal-title"
          @click=${(e: Event) => e.stopPropagation()}
        >
          <div class="diagram-modal-header">
            <div>
              <h3 id="ead-diagram-modal-title">EAD Diagram</h3>
              <div class="subtitle">EAD Skill Key: ${draft.skillKey}</div>
            </div>
            <div class="detail-toolbar-actions">
              <button
                class="btn btn-sm"
                ?disabled=${!hasScript}
                @click=${() => this._regenerateDiagramFromScript()}
              >
                Regenerate
              </button>
              <button class="btn" @click=${() => this._closeDiagramModal()}>Close</button>
            </div>
          </div>
          <div class="diagram-modal-body">
            <ead-mermaid-diagram
              .source=${mermaidSource}
              .showHeading=${false}
              .showSourceToggle=${true}
              .embedded=${true}
              .refreshKey=${this._diagramRefreshKey}
            ></ead-mermaid-diagram>
          </div>
        </div>
      </div>
    `;
  }

  private _renderViewPanel() {
    const draft = this._draft!;
    const script = skillScriptFor(draft);
    const reportMarkdown = buildSkillReportMarkdown(draft, { includeMermaid: false, includeScript: false });
    const scriptMarkdown = buildSkillScriptBlockMarkdown(script);
    return html`
      <div class="detail-toolbar">
        <div class="detail-toolbar-title">
          <h3>
            Skill Report
            ${isViewOnlySkill(draft) ? html`<span class="badge">View only</span>` : nothing}
          </h3>
          <div class="subtitle">EAD Skill Key: ${draft.skillKey}</div>
        </div>
        <div class="detail-toolbar-actions">
          ${this._canEdit ? html`<button class="btn" @click=${() => this._enterEdit()}>Edit</button>` : nothing}
          ${this._renderToolbarMenu()}
        </div>
      </div>
      <div class="detail-body">
        <div class="skill-view-scroll">
          <div class="skill-view-panel">
            <ead-skill-report-view .markdown=${reportMarkdown} .embedded=${true}></ead-skill-report-view>
            <div class="ead-script-section">
              <div class="ead-script-header">
                <h2>EAD Script</h2>
                <div class="ead-script-actions">
                  <button class="btn btn-sm" @click=${() => this._openGenerateModal()}>Generate</button>
                  <button
                    class="btn btn-sm"
                    ?disabled=${!this._hasDiagramSource(draft)}
                    @click=${() => this._openDiagramModal()}
                  >
                    View Diagram
                  </button>
                </div>
              </div>
              ${script
                ? html`<ead-skill-report-view .markdown=${scriptMarkdown} .embedded=${true}></ead-skill-report-view>`
                : html`<div class="empty-script">No EAD Script yet. Click Generate to create one from the skill description.</div>`}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private _renderEditPanel() {
    const draft = this._draft!;
    const script = skillScriptFor(draft);
    return html`
      <div class="detail-toolbar">
        <div class="detail-toolbar-title">
          <h3>Edit Skill</h3>
          <div class="subtitle">EAD Skill Key: ${draft.skillKey}</div>
        </div>
        <div class="detail-toolbar-actions">
          ${this._dirty ? html`<span class="dirty-inline">Unsaved</span>` : nothing}
          <button class="btn" ?disabled=${this._saving} @click=${() => this._exitEdit()}>Cancel</button>
          <button class="btn btn-primary" ?disabled=${!this._dirty || this._saving} @click=${() => this._save()}>
            ${this._saving ? "Saving…" : "Save"}
          </button>
          ${this._renderToolbarMenu()}
        </div>
      </div>

      <div class="detail-body">
        <div class="edit-scroll">
          <div class="field-group">
            <label>Skill Title</label>
            <input
              type="text"
              .value=${draft.displayName}
              @input=${(e: Event) => this._updateDraft({ displayName: (e.target as HTMLInputElement).value })}
            />
          </div>

          <div class="field-group">
            <label>Skill Description</label>
            <div class="readonly-hint">Markdown supported</div>
            <textarea
              class="body"
              .value=${draft.bodyMarkdown || ""}
              @input=${(e: Event) => this._updateDraft({ bodyMarkdown: (e.target as HTMLTextAreaElement).value })}
            ></textarea>
          </div>

          <div class="field-group">
            <label>EAD Script</label>
            <textarea
              class="script"
              .value=${script}
              @input=${(e: Event) => this._updateSkillScript((e.target as HTMLTextAreaElement).value)}
            ></textarea>
            <div class="ead-script-actions" style="margin-top: 10px;">
              <button class="btn btn-sm" @click=${() => this._openGenerateModal()}>Generate</button>
              <button
                class="btn btn-sm"
                ?disabled=${!this._hasDiagramSource(draft)}
                @click=${() => this._openDiagramModal()}
              >
                View Diagram
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <div class="container">
        <div class="breadcrumb">EAD Explore &gt; EAD Skills</div>
        <div class="layout">
          <aside class="skill-list">
            <div class="skill-list-header"><h2>EAD Skills</h2></div>
            ${this._loading && !this._summaries.length
              ? html`<div class="loading">Loading…</div>`
              : this._summaries.map(
                  (s) => html`
                    <div
                      class="skill-item ${s.skillKey === this._selectedKey ? "selected" : ""}"
                      @click=${() => this._selectSkill(s.skillKey)}
                    >
                      <div class="name">${s.displayName}</div>
                      <div class="key-label">EAD Skill Key</div>
                      <div class="key">${s.skillKey}</div>
                    </div>
                  `,
                )}
          </aside>
          <section class="detail-panel">
            ${this._error ? html`<p class="dirty">${this._error}</p>` : nothing}
            ${this._detailLoading
              ? html`<div class="empty">Loading skill…</div>`
              : !this._draft
                ? html`<div class="empty">Select a policy skill.</div>`
                : this._editMode
                  ? this._renderEditPanel()
                  : this._renderViewPanel()}
          </section>
        </div>
        ${this._draft ? this._renderDiagramModal(this._draft) : nothing}
        ${this._draft ? this._renderGenerateModal(this._draft) : nothing}
        ${this._draft ? this._renderScriptPreviewModal(this._draft) : nothing}
        ${this._renderDeleteConfirmModal()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ead-skills-view": EadSkillsView;
  }
}
